/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import isString from 'lodash.isstring';
import APM from '../APM';
import {send} from '../beacons/Beacon';
import Logger from '../logger/PerformanceMonitorLogger';
import PerformanceMeasurement from '../measurement/PerformanceMeasurement';
import ServiceMeasurements from '../measurement/ServiceMeasurements';
import {
  Blacklist,
  Duplications,
  IPerformanceMonitor,
  MonitorCategory,
  MonitorResults,
  ServiceResults,
  ServiceTimings
} from '../types/types';
import BaseMonitor from './BaseMonitor';

/**
 * The `PerformanceMonitor` class provides the base class implementation for
 * performing comprehensive monitoring of system performance metrics across
 * components and services within an iOn class application.
 *
 * The API includes mechanisms for starting, stopping, and managing performance
 * measurements, while segmenting service-specific metrics.
 */
export default class PerformanceMonitor extends BaseMonitor implements IPerformanceMonitor {
  /**
   * Provides a reference to the `PerformanceMeasurement` instance which is
   * used for measuring the overall monitoring of the instance.
   */
  protected _measure: PerformanceMeasurement;

  /**
   * Provides a reference to the `ServiceMeasurements` instances for each
   * service being monitored.
   */
  protected _services: Map<string, ServiceMeasurements>;

  /**
   * A boolean indicating if the monitoring should automatically complete
   * after a certain condition is met.
   */
  protected _autoComplete: boolean;

  /**
   * Provides a reference to a list of services that are blacklisted from
   * being monitored.
   */
  protected _blacklist: Blacklist;

  /**
   * Provides a reference to a list of services that have been duplicated
   * (invoked more than once) during the monitoring process.
   */
  protected _duplicated: Duplications;

  /**
   * The `PerformanceMonitor` constructor initializes the monitor for a specific
   * category.
   *
   * @param category The category under which this monitor operates, which is
   *        used for providing categorization within the underlying metrics.
   */
  constructor(category: MonitorCategory) {
    super(category);
    this._services     = new Map();
    this._autoComplete = false;
    this._blacklist    = {};
    this._duplicated   = {};
  }

  /**
   * Provides a `autoComplete` getter which returns `true` if the Performance
   * Monitor instance is set to automatically complete upon completion of all
   * monitored services and components.
   *
   * ```typescript
   *  if (monitor.autoComplete) {
   *    ...
   *  }
   * ```
   *
   * @returns `true` if the Performance Monitor instance is set to automatically
   *          complete.
   */
  get autoComplete(): boolean {
    return this._autoComplete;
  }

  /**
   * Allows for setting the autoComplete state of the Performance Monitor
   * instance which, when `true`, ensures that the monitor is automatically
   * stopped when all monitored services and components have completed.
   *
   * ```typescript
   *  // set the current autoComplete state of the monitor instance ...
   *  monitor.autoComplete = true;;
   * ```
   *
   * @param `true` if the Performance Monitor instance is set to automatically
   *         complete.
   */
  set autoComplete(value: boolean) {
    this._autoComplete = value;
  }

  /**
   * Allows for determining if the `PerformanceMonitor` instance is currently
   * running.
   *
   * ```typescript
   *  if (monitor.isRunning()) {
   *    ...
   *  }
   * ```
   *
   * @returns `true` if the `PerformanceMonitor` instance is currently running,
   *          otherwise, `false`.
   */
  isRunning(): boolean {
    return this._measure?.isRunning();
  }

  /**
   * Initiates the performance monitoring of the instance, if the monitor is
   * currently running, the method start operation will be omitted (default
   * behavior). To override the default behavior, the `force` argument can
   * be set to `true`, in which case monitoring will be restarted.
   *
   * ```typescript
   *  if (monitor.start()) {
   *    ...
   *  }
   * ```
   *
   * @param   context Optional context in which the monitor is to be associated.
   * @param   force A boolean indicating if the monitoring should be forced to
   *          restart if it is currently  running.
   * @returns `true` if the monitoring was started successfully, otherwise `false`.
   */
  start(context?: string, force = true): boolean {
    if (this.verify('start')) {
      if (!this.isRunning() || force) {
        this.reset(context);
        return true;
      } else if (Logger.isEnabled) {
        this._logger.monitorStartWarning();
      }
    }
    return false;
  }

  /**
   * Immediately begins monitoring a specific service for a given url provided the
   * service has not been blacklisted, and is not currently being monitored. This
   * method begins the performance measurement for the given service url, and the
   * measurement is completed upon the `stopService` method being invoked with the
   * given service url.
   *
   * Note, generally, this method is automatically invoked via service client
   * interceptors and typically need not be invoked directly by clients.
   *
   * ```typescript
   *  monitor.startService('/connect/services/assets/123');
   * ```
   *
   * @param url The URL of the service which is to begin being monitored.
   */
  startService(url: string) {
    if (this.verify('startService')) {
      if (!this.isBlackListed(url)) {
        let measurements = this._services.get(url);
        if (!measurements) {
          measurements = new ServiceMeasurements(url);
          measurements.start();
          this._services.set(url, measurements);
          Logger.isEnabled && this._logger.serviceStarted(url);
        } else {
          measurements.start();
          this._duplicated[url] = true;
          Logger.isEnabled && this._logger.serviceStartWarning(url);
        }
      } else {
        Logger.isEnabled && this._logger.serviceBlacklistWarning(url);
      }
    }
  }

  /**
   * Stops monitoring the specific service url and finalizes the service
   * operations measurement within the context of the monitored services.
   *
   * Note, generally, this method is automatically invoked via service
   * client interceptors and typically need not be invoked directly by
   * clients.
   *
   * ```typescript
   *  monitor.stopService('/connect/services/assets/123');
   * ```
   *
   * @param url The name of the service to stop monitoring.
   */
  stopService(url: string) {
    if (this.verify('stopService')) {
      const measurements: ServiceMeasurements = this._services.get(url);
      if (measurements?.hasRunning()) {
        const duration = measurements.stop();
        Logger.isEnabled && this._logger.serviceStopped(url, duration);
      } else if (measurements) {
        Logger.isEnabled && this._logger.serviceStopWarning(url);
      } else if (this.isBlackListed(url)) {
        Logger.isEnabled && this._logger.serviceBlacklistWarning(url, false);
      }
      if (this._autoComplete && !this.hasRunningServices()) {
        this.stop();
      }
    }
  }

  /**
   * Determines if the specific service url is currently being monitored
   * (running).
   *
   * Note, generally, this method is automatically invoked via service
   * client interceptors and typically need not be invoked directly by
   * clients.
   *
   * ```typescript
   *  if (monitor.isServiceRunning('/connect/services/assets/123')) {
   *    ...
   *  }
   * ```
   *
   * @param    url The url of the service for which to determine if it is
   *           currently running.
   * @returns `true` if the service is currently being monitored (running),
   *           otherwise `false`.
   */
  isServiceRunning(url: string): boolean {
    return this._services.get(url)?.hasRunning();
  }

  /**
   * Determines if there are any services currently being monitored.
   *
   * Note, generally, this method is automatically invoked via service
   * client interceptors and typically need not be invoked directly by
   * clients.
   *
   * ```typescript
   *  if (monitor.hasRunningServices()) {
   *    ...
   *  }
   * ```
   *
   * @returns `true` if there are any services currently being monitored,
   *          otherwise `false`.
   */
  hasRunningServices(): boolean {
    let hasRunning = false;
    this._services?.forEach((measurement: ServiceMeasurements) => {
      if (measurement.hasRunning()) {
        hasRunning = true;
      }
    });
    return hasRunning;
  }

  /**
   * Determines if the given service `url` is currently being monitored by this
   * `PerformanceMonitor` instance.
   *
   * @param   url The service url to be verified for monitoring.
   * @returns `true` if the given service url is being monitored by this instance.
   */
  hasService(url: string): boolean {
    return !!this._services.get(url);
  }

  /**
   * Immediately stops the monitoring processes for the `PerformanceMonitor`
   * instance. If invoked with the `isAutoStop` argument set to true, the
   * monitor will be stopped even if there are currently running items being
   * monitored
   *
   * ```typescript
   *  const totalDuration = monitor.stop();
   * ```
   *
   * @param   isAutoStop A boolean indicating if monitoring should be stopped
   *          even if there are currently running items being being monitored
   *          (defaults to false).
   * @returns The total duration of the `PerformanceMonitor` instance.
   */
  stop(isAutoStop = false): number {
    if (this.verify('stop')) {
      if (this._measure?.isRunning() || isAutoStop) {
        this._measure?.stop();
        if (isAutoStop && Logger.isEnabled) {
          this._logger.monitorAutoStopWarning();
        }
        const {
          runningServices,
          measuredServices,
          parallelServiceTime,
          cumulativeServiceTime,
          serviceTimings
        }: ServiceResults = this.finalizeServicesResults();
        if (runningServices?.length) {
          Logger.isEnabled && this._logger.monitorStopServicesWarning(runningServices);
        }
        const totalTime   = this._measure.duration;
        const totalUITime = parallelServiceTime ? totalTime - parallelServiceTime : totalTime;
        const results: MonitorResults = {
          duplications          : Object.keys(this._duplicated),
          totalTime             : PerformanceMeasurement.getFixedTime(totalTime),
          totalUITime           : PerformanceMeasurement.getFixedTime(totalUITime),
          serviceTime           : PerformanceMeasurement.getFixedTime(parallelServiceTime),
          cumulativeServiceTime : PerformanceMeasurement.getFixedTime(cumulativeServiceTime),
          serviceTimings,
          measuredServices
        };
        Logger.isEnabled && this._logger.monitorStopped(results);
        send(this._category, this._context, results);
      } else {
        Logger.isEnabled && this._logger.monitorStopWarning();
      }
      return this._measure?.duration;
    }
  }

  /**
   * Updates the `PerformanceMonitor` instance's service blacklist. Items added
   * to the blacklist will be omitted from monitoring. This can be useful if a
   * separate monitor is being used to measure specific services, in which case
   * the service may not need to be included within this monitor instance.
   *
   * The `blackList` method can be used to either add a new item to the blacklist,
   * or replace all existing items in the blacklist (useful for when monitoring
   * is to be restarted in a different context, etc.).
   *
   * ```typescript
   *  // add an item to the current blacklist ...
   *  monitor.blackList('device-services/devices/smartsearch');
   *
   *  // override the current blacklist and initialize with the given items ...
   *  monitor.blackList({
   *    'device-services/devices/smartsearch': true
   *  }, true);
   * ```
   *
   * @param item A `Blacklist` instance or service url string representing the
   *        service to be blacklisted.
   * @param override A boolean indicating if the existing blacklist should be
   *        overridden with the provided `Blacklist`.
   */
  blackList(item: Blacklist | string, override = false) {
    if (!isString(item)) {
      if (!override) {
        this._blacklist = {...this._blacklist, ...item as Blacklist};
      } else {
        this._blacklist = item as Blacklist;
      }
    } else {
      this._blacklist[item as string] = true;
    }
  }

  /**
   * Checks if a specific service url is currently blacklisted from being
   * monitored by this instance.
   *
   * ```typescript
   *  if (monitor.isBlackListed('reporting/jasperOptions')) {
   *    ...
   *  }
   * ```
   *
   * @param    url The service url for which to determine if it has been
   *           blacklisted .
   * @returns `true` if the service is blacklisted, otherwise `false`.
   */
  isBlackListed(url: string): boolean {
    return !!this._blacklist[url];
  }

  /**
   * @private
   *
   * Finalizes the results of all measured services by ensuring each service is
   * stopped (if currently running) and returning the complete picture of the
   * service performance for this monitor run, including a list of measured
   * services, a list of services which were still running when invoked (if any),
   * the total parallel execution time of all services, and the cumulative time
   * of all services.
   *
   * @returns A complete picture of the service performance for this monitor run,
   *          which includes all measured services, any services that were running
   *          when invoked, the total parallel execution time of all services, and
   *          the cumulative time of all services.
   */
  private finalizeServicesResults(): ServiceResults {
    const totalServices: ServiceMeasurements[] = [];
    const runningServices: string[]  = [];
    const measuredServices: string[] = [];
    let serviceTimings: ServiceTimings = {};
    let cumulativeServiceTime = 0;
    this._services?.forEach((items: ServiceMeasurements, service: string) => {
      serviceTimings = {...serviceTimings, ...items.getResults()};
      if (items?.hasRunning()) {
        items.stop(true);
        runningServices.push(service);
      }
      totalServices.push(items);
      measuredServices.push(service);
      cumulativeServiceTime += items.cumulativeDuration;
    });
    const serviceStart = totalServices[0]?.startTime;
    const lastService  = totalServices[totalServices.length-1];
    if (lastService?.hasRunning()) {
      lastService?.stop(true);
    }
    const serviceStop  = lastService?.endTime;
    const parallelServiceTime = serviceStop - serviceStart;
    if (runningServices.length) {
      Logger.isEnabled && this._logger.monitorStopServicesWarning(runningServices);
    }
    return {
      runningServices,
      measuredServices,
      parallelServiceTime,
      cumulativeServiceTime,
      serviceTimings
    };
  }

  /**
   * @private
   *
   * Determines if Global Application Performance Monitoring is enabled,
   * returning `true`, otherwise `false`.
   */
  private verify(method: string): boolean {
    if (!APM.isEnabled && Logger.isEnabled) {
      this._logger.monitorDisabledError(method);
    }
    return APM.isEnabled;
  }

  /**
   * Provides an internal utility which allows for resetting the state of
   * the `PerformanceMonitor` instance.
   */
  private reset(context?: string) {
    this._measure      = new PerformanceMeasurement(true);
    this._services     = new Map();
    this._blacklist    = {};
    this._duplicated   = {};
    this._autoComplete = false;
    this.context       = context;
    Logger.isEnabled && this._logger.monitorStarted();
  }
}
